import { useState } from 'react';
import {
  deletionOutcome,
  pendingDeletionActions,
  type JobOrder,
} from '@/domain';
import { useSession } from '@/application/usecases/useSession';
import {
  useApproveDeletion,
  useRequestDeletion,
  useRestoreCase,
  useWithdrawDeletion,
} from '@/application/usecases/useOrders';
import { formatDay } from '@/shared/format/instant';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { Decision } from '@/ui/primitives/Decision';
import { toast } from '@/ui/primitives/toastStore';
import { HoldNote } from '@/ui/data/StatusDot';
import { drawerStyles } from '@/ui/layout/Drawer';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { DeleteCaseDialog } from './DeleteCaseDialog';

/*
 * Deleting a case.
 *
 * What a person may do is read from the permissions the server resolved and the
 * one domain rule, never from a role: a holder of `deleteCase` sees Delete case;
 * with a deletion waiting, another holder of `approveDeletion` sees Approve and
 * Refuse, and the person who asked sees Withdraw; on a deleted case, a holder of
 * `approveDeletion` sees Restore.
 *
 * `onDeleted` runs once the case has actually gone, so a drawer can close on a
 * list the case is no longer on. The case record stays open, because a deleted
 * case still has its record.
 *
 * Since the request of 5 October 2026 (third), section 2, each decision is
 * asked about first: Approve, Refuse, Withdraw and Restore through the shared
 * confirmation, and Delete case in its own dialog, which is one.
 */

interface DeletionProps {
  order: JobOrder;
  onDeleted?: () => void;
}

/** The order drawer's deletion block, under the dates. */
export function DeletionControls({ order, onDeleted }: DeletionProps) {
  const session = useSession();
  const outcome = deletionOutcome(session.data?.permissions ?? []);

  if (order.deletion.status !== 'none') {
    return <DeletionNote order={order} onDeleted={onDeleted} className={drawerStyles.deletion} />;
  }
  if (outcome === null) return null;

  return (
    <div className={drawerStyles.deletion}>
      <DeleteCaseButton order={order} onDeleted={onDeleted} />
    </div>
  );
}

/**
 * The deletion on a case, while there is one: who asked for it or deleted it,
 * when, why, and the decisions the caller may take on it. Nothing for a case
 * with no deletion on it.
 */
export function DeletionNote({
  order,
  onDeleted,
  className,
}: DeletionProps & { className?: string }) {
  const session = useSession();
  const permissions = session.data?.permissions ?? [];
  const userId = session.data?.user.id ?? '';

  const withdraw = useWithdrawDeletion();
  const approve = useApproveDeletion();
  const restore = useRestoreCase();
  const ask = useConfirmAction();
  const busy = withdraw.isPending || approve.isPending || restore.isPending;

  const { deletion, jobOrderNo, rowVersion } = order;
  const actions = pendingDeletionActions(deletion, userId, permissions);
  const day = (instant: string | null) => (instant === null ? '' : formatDay(instant));

  if (deletion.status === 'none') return null;

  const note =
    deletion.status === 'deleted'
      ? [
          `${strings.deletion.deletedOn} ${day(deletion.deletedAt)}`,
          `${strings.deletion.by} ${deletion.decidedByUserName ?? ''}`,
          `· ${strings.deletion.purgesOn} ${deletion.purgesAt ?? ''}`,
        ].join(' ')
      : // "Deletion asked for by Mohamed Arabia on 2026-09-13", with the reason under it.
        [
          strings.deletion.pendingBy,
          deletion.requestedByUserName ?? '',
          strings.deletion.on,
          day(deletion.requestedAt),
        ].join(' ');

  const mayRestore = deletion.status === 'deleted' && permissions.includes('approveDeletion');
  const command = { jobOrderNo, rowVersion };

  return (
    <div className={className}>
      <HoldNote>{note}</HoldNote>
      {deletion.reason !== null && <p className={drawerStyles.deletionReason}>{deletion.reason}</p>}

      {(actions.approve || actions.refuse || actions.withdraw || mayRestore) && (
        <div className={drawerStyles.deletionActions}>
          {/*
            Approve and Refuse answer one question, so they move together or not
            at all. Section 9 of the request of 16 September 2026: the client
            reported one of them dropping to the next line.
          */}
          <Decision label={strings.deletion.decisionLabel}>
          {actions.approve && (
            <Button
              variant="primary"
              disabled={busy}
              onClick={() =>
                ask(
                  strings.confirm.approveDeletion(jobOrderNo),
                  () =>
                    approve.mutateAsync(command).then(() => {
                      toast(`${jobOrderNo} ${strings.deletion.approvedToast}`);
                      onDeleted?.();
                    }),
                  { confirmLabel: strings.deletion.approve },
                )
              }
            >
              {strings.deletion.approve}
            </Button>
          )}
          {(actions.refuse || actions.withdraw) && (
            <Button
              disabled={busy}
              onClick={() =>
                ask(
                  actions.withdraw
                    ? strings.confirm.withdrawDeletion(jobOrderNo)
                    : strings.confirm.refuseDeletion(jobOrderNo),
                  () =>
                    withdraw.mutateAsync(command).then(() => {
                      toast(
                        `${jobOrderNo} ${
                          actions.withdraw ? strings.deletion.withdrawnToast : strings.deletion.refusedToast
                        }`,
                      );
                    }),
                  {
                    confirmLabel: actions.withdraw ? strings.deletion.withdraw : strings.deletion.refuse,
                  },
                )
              }
            >
              {actions.withdraw ? strings.deletion.withdraw : strings.deletion.refuse}
            </Button>
          )}
          </Decision>
          {mayRestore && (
            <Button
              disabled={busy}
              onClick={() =>
                ask(
                  strings.confirm.restoreCase(jobOrderNo),
                  () =>
                    restore.mutateAsync(command).then(() => {
                      toast(`${jobOrderNo} ${strings.deletion.restoredToast}`);
                    }),
                  { confirmLabel: strings.deletion.restore },
                )
              }
            >
              {strings.deletion.restore}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Delete case, or Ask for deletion, and the dialog that takes the reason. A
 * bare button, so it can sit in any row of actions; nothing for a caller
 * without `deleteCase` or on a case that already has a deletion on it.
 */
export function DeleteCaseButton({ order, onDeleted }: DeletionProps) {
  const session = useSession();
  const [asking, setAsking] = useState(false);
  const requestDeletion = useRequestDeletion();

  const { deletion, jobOrderNo, rowVersion } = order;
  const outcome = deletionOutcome(session.data?.permissions ?? []);
  if (outcome === null || deletion.status !== 'none') return null;

  return (
    <>
      <Button onClick={() => setAsking(true)}>{strings.deletion.delete}</Button>

      {asking && (
        <DeleteCaseDialog
          jobOrderNo={jobOrderNo}
          outcome={outcome}
          busy={requestDeletion.isPending}
          error={requestDeletion.error === null ? null : failureWords(requestDeletion.error)}
          onCancel={() => {
            setAsking(false);
            requestDeletion.reset();
          }}
          onConfirm={(reason) =>
            requestDeletion.mutate(
              { jobOrderNo, rowVersion, reason },
              {
                onSuccess: (saved) => {
                  setAsking(false);
                  if (saved.deletion.status === 'deleted') {
                    toast(`${jobOrderNo} ${strings.deletion.deletedToast}`);
                    onDeleted?.();
                  } else {
                    toast(`${jobOrderNo} ${strings.deletion.requestedToast}`);
                  }
                },
              },
            )
          }
        />
      )}
    </>
  );
}
